/**
 * @module
 * @description Javascript that drives the accordion content module
 */

import { on } from '@utils/events';

import * as slide from '@utils/dom/slide';

const options = {
	speed: 200,
};
const classes = {
	active: 'acc-active',
};

const selectors = {
	group_toggle: '[data-js="c-details-group-toggle"]',
	accordion: '[data-js="c-accordion"]',
	details: '[data-js="c-details"]',
	content: '[data-js="c-details-content"]',
};

const accordions = document.querySelectorAll(selectors.details);
const accordion_group_toggles = document.querySelectorAll(selectors.group_toggle);

const getGroup = (element) => {
	return element.closest(selectors.accordion);
};

const getDetailsGroupToggle = (details) => {
	const accordion = getGroup(details);
	if (!accordion) return null;
	return accordion.querySelector(selectors.group_toggle);
};

const getGroupToggleButtonDetails = (group_toggle) => {
	const group = getGroup(group_toggle);
	if (!group) {
		return null;
	}
	const group_details = group.querySelectorAll(selectors.details);
	if (!group_details) {
		return null;
	}
	return group_details;
};

/**
 * Pass the group toggle button.
 * @param {*} group
 * @returns boolean
 */
const isAllGroupDetailsOpen = (group_toggle) => {
	const group = getGroup(group_toggle);
	if (!group) {
		return false;
	}
	const group_details = group.querySelectorAll(selectors.details);
	return Array.from(group_details).every((details) => isDetailsOpen(details));
};

/**
 * When a <details> is toggled, checks if it's part
 * of a group and updates the group toggle accordingly.
 *
 * @param {*} details
 */
const checkGroupToggle = (details) => {
	const group_toggle = getDetailsGroupToggle(details);
	if (!group_toggle) {
		return;
	}

	// Are all group <details> open?
	// If so, we want to show "Collapse all" button.
	// Otherwise, we want to show "Expand all" button.
	const all_open = isAllGroupDetailsOpen(group_toggle);

	setGroupToggleState(group_toggle, all_open);
};

const setGroupToggleState = (group_toggle, expanded = null) => {
	const group_details = getGroupToggleButtonDetails(group_toggle);
	if (!group_details) {
		return;
	}

	// If no expanded value is passed, we will determine it.
	if (expanded !== true && expanded !== false) {
		// Are all group <details> open?
		// If so, we want to show "Collapse all" button.
		// Otherwise, we want to show "Expand all" button.
		expanded = isAllGroupDetailsOpen(group_toggle);
	}

	const current_expanded = group_toggle.getAttribute('aria-expanded') === 'true';
	if (current_expanded === expanded) {
		return;
	}

	// Set new <button> text.
	const textAttr = expanded ? 'data-text-expanded' : 'data-text-collapsed';
	const newText = group_toggle.getAttribute(textAttr);
	if (newText) {
		group_toggle.innerText = newText;
	}

	// Set new <button> labels.
	const labelAttr = expanded ? 'data-label-expanded' : 'data-label-collapsed';
	const newLabel = group_toggle.getAttribute(labelAttr);
	if (newLabel) {
		group_toggle.setAttribute('aria-label', newLabel);
		group_toggle.setAttribute('title', newLabel);
	}

	group_toggle.setAttribute('aria-expanded', expanded);

	// Set expanded <button> class.
	group_toggle.classList.toggle('expanded', expanded);
};

/**
 * We only "toggle" the group when the user clicks the toggle button.
 *
 * @param {*} group_toggle
 * @returns
 */
const toggleGroupToggle = (group_toggle) => {
	const group_details = getGroupToggleButtonDetails(group_toggle);
	if (!group_details) {
		return;
	}

	// Are all group <details> open?
	// If so, we want to show "Collapse all" button.
	// Otherwise, we want to show "Expand all" button.
	const all_open = isAllGroupDetailsOpen(group_toggle);

	setGroupToggleState(group_toggle, !all_open);

	// Open or close all <details>
	group_details.forEach((details) => (details.open = !all_open));
};

/**
 * Setup expand/collapse all group <button>s.
 */
const initGroupToggles = () => {
	accordion_group_toggles.forEach((group_toggle) => {
		// No use processing if no group.
		const group = getGroup(group_toggle);
		if (!group) {
			return;
		}

		// Are all group <details> open?
		// If so, we want to show "Collapse all" button.
		// Otherwise, we want to show "Expand all" button.
		const all_open = isAllGroupDetailsOpen(group_toggle);

		// Make the <button> "active".
		group_toggle.classList.add(classes.active);

		// This will set the button properties.
		setGroupToggleState(group_toggle, all_open);

		group_toggle.addEventListener('click', (e) => toggleGroupToggle(e.target));
	});
};

const isSlideAccordion = (details) => {
	return details.getAttribute('data-slide') === '1';
};

/**
 * DO NOT try to trigger openAccordion().
 * Simply get the details DOM element and set the `open` property to `true`.
 * e.g.
 * details.open = true
 *
 * @function openAccordion
 * @description Toggle the accordion open
 */

const openAccordion = (details) => {
	try {
		details.open = true;
	} finally {
		details.classList.add(classes.active);
	}
	checkGroupToggle(details);
	if (isSlideAccordion(details)) {
		const content = details.querySelector(selectors.content);
		if (content) {
			slide.down(content, content.id, options.speed);
		}
	}
};

/**
 * DO NOT try to trigger closeAccordion().
 * Simply get the details DOM element and set the `open` property to `false`.
 * e.g.
 * details.open = false
 *
 * @function closeAccordion
 * @description Toggle the accordion closed
 */

const closeAccordion = (details) => {
	try {
		details.open = false;
	} finally {
		details.classList.remove(classes.active);
	}
	checkGroupToggle(details);
	if (isSlideAccordion(details)) {
		const content = details.querySelector(selectors.content);
		if (content) {
			slide.up(content, content.id, options.speed);
		}
	}
};

const isDetailsOpen = (details) => {
	try {
		return details.open;
	} catch {
		return details.classList.contains(classes.active);
	}
};

/**
 * @function toggleDetails
 * @param {Object} e The js event object.
 * @description Toggle the active accordion item using class methods.
 */

const toggleDetails = (e) => {
	const details = e.target;
	const is_open = isDetailsOpen(details);

	if (is_open) {
		openAccordion(details);
	} else {
		closeAccordion(details);
	}
};

/**
 * @function bindEvents
 * @description Bind the events for this module here.
 */
const bindEvents = () => {
	accordions.forEach((details) => {
		// Listen to <details> toggle event.
		on(details, 'toggle', toggleDetails);

		// Close <details> on ESC key.
		details.addEventListener('keydown', (e) => {
			if (isDetailsOpen(details) && e.key === 'Escape') {
				details.open = false;
			}
		});
	});
};

/**
 * "Enhance" accordions means only setting CSS when JS is enabled.
 */
const enhanceAccordions = () => {
	accordions.forEach((details) => details.classList.add('acc-enhanced'));
};

/**
 * @function init
 * @description Initializes the class if the element(s) to work on are found.
 */
const init = () => {
	enhanceAccordions();
	bindEvents();
	initGroupToggles();
};

export default init;
